import React, { useState, useEffect } from "react";
import { knowledgeAPI } from "../../services/api";
import { motion, AnimatePresence } from "framer-motion";

const VideoSection = () => {
  const [allVideos, setAllVideos] = useState([]);
  const [displayVideos, setDisplayVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeVideo, setActiveVideo] = useState(null);

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        // ดึงข้อมูลมาทั้งหมดก่อน
        const response = await knowledgeAPI.getAll({ limit: 50 });
        const videos = response.data.data;
        setAllVideos(videos);

        // สุ่ม 4 รายการ
        const shuffled = shuffleArray(videos);
        const selected = shuffled.slice(0, 4);
        setDisplayVideos(selected);

        // ตั้งค่าวิดีโอแรกเป็น active
        if (selected.length > 0) {
          setActiveVideo(selected[0]);
        }
      } catch (error) {
        console.error("Failed to fetch videos:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  // ฟังก์ชันสุ่มลำดับ array
  const shuffleArray = (array) => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  };

  // ฟังก์ชันสุ่ม 4 รายการใหม่
  const shuffleVideos = () => {
    if (allVideos.length > 0) {
      const shuffled = shuffleArray(allVideos);
      const selected = shuffled.slice(0, 4);
      setDisplayVideos(selected);
      setActiveVideo(selected[0]);
    }
  };

  const getYoutubeEmbedUrl = (url) => {
    if (!url) return "";
    const patterns = [
      /youtube\.com\/watch\?v=([^&]+)/,
      /youtu\.be\/([^?]+)/,
      /youtube\.com\/embed\/([^?]+)/,
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) {
        return `https://www.youtube.com/embed/${match[1]}`;
      }
    }
    return url;
  };

  const getYoutubeThumbnail = (url) => {
    const patterns = [
      /youtube\.com\/watch\?v=([^&]+)/,
      /youtu\.be\/([^?]+)/,
      /youtube\.com\/embed\/([^?]+)/,
    ];
    for (const pattern of patterns) {
      const match = url?.match(pattern);
      if (match) {
        return `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg`;
      }
    }
    return "https://via.placeholder.com/320x180?text=No+Video";
  };

  if (loading) {
    return (
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center">
            <div className="spinner"></div>
          </div>
        </div>
      </section>
    );
  }

  if (displayVideos.length === 0) {
    return null;
  }

  return (
    <section className="py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h1
            className="text-3xl md:text-4xl font-bold text-primary-500 relative inline-block pb-4"
            style={{
              borderBottom: "3px solid #FFBF07",
            }}
          >
            <i className="fas fa-video text-secondary-500 mr-2"></i>
            วีดีโอองค์ความรู้
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto mt-4">
            รวมวีดีโอองค์ความรู้และกิจกรรมบริการวิชาการ
          </p>
        </motion.div>

        {/* Video Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* วิดีโอใหญ่ (ซ้าย - 2 คอลัมน์) */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeVideo?.knowledge_id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.3 }}
                className="lg:col-span-2 cursor-pointer"
                onClick={() => activeVideo && setActiveVideo(activeVideo)}
              >
                <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-black aspect-video h-full">
                  <iframe
                    src={getYoutubeEmbedUrl(activeVideo?.main_vdo_url)}
                    title={activeVideo?.title}
                    className="absolute top-0 left-0 w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* วิดีโอเล็ก (ขวา - 1 คอลัมน์) */}
            <div className="lg:col-span-1 space-y-4">
              {displayVideos
                .filter((v) => v.knowledge_id !== activeVideo?.knowledge_id)
                .slice(0, 3)
                .map((video, index) => (
                  <motion.div
                    key={video.knowledge_id}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    whileHover={{ scale: 1.02 }}
                    className="cursor-pointer rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all bg-white"
                    onClick={() => setActiveVideo(video)}
                  >
                    <div className="flex gap-3 p-2">
                      <div className="relative w-24 h-16 flex-shrink-0 rounded-lg overflow-hidden bg-gray-900">
                        <img
                          src={getYoutubeThumbnail(video.main_vdo_url)}
                          alt={video.title}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src =
                              "https://via.placeholder.com/320x180?text=No+Video";
                          }}
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                          <div className="w-6 h-6 bg-secondary-500 rounded-full flex items-center justify-center">
                            <i className="fas fa-play text-primary-500 text-xs"></i>
                          </div>
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-gray-800 line-clamp-2 mb-1">
                          {video.title}
                        </h4>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <span className="flex items-center">
                            <i className="fas fa-eye text-primary-500 mr-1"></i>
                            {video.views || 0}
                          </span>
                          <span className="flex items-center">
                            <i className="fas fa-calendar-alt text-primary-500 mr-1"></i>
                            {new Date(video.publish_date).toLocaleDateString(
                              "th-TH",
                              {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              },
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default VideoSection;
